// src/components/auth/ForgotPassword.js
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { authService } from '../../services/authService';
import { usePageTitle } from '../../hooks/usePageTitle';
import { Logo } from '../../hooks/useLogo';

export const ForgotPassword = () => {
    usePageTitle('Forgot Password');
    const [email, setEmail] = useState('');
    const [status, setStatus] = useState('form'); // 'form', 'loading', 'success', 'error'
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!email.trim()) {
            setError('Email is required');
            return;
        }

        if (!/\S+@\S+\.\S+/.test(email)) {
            setError('Please enter a valid email address');
            return;
        }

        setStatus('loading');
        setError('');

        try {
            const response = await authService.forgotPassword(email);
            setStatus('success');
            setMessage(response.message || 'Password reset link sent to your email');
        } catch (error) {
            setStatus('error');
            setError(error.message || 'Failed to send reset email. Please try again.');
            // Don't set generic message, use the actual error
        }
    };

    if (status === 'success') {
        return (
            <div className="forgot-password-page">
                <div className="forgot-password-container">
                    <div className="forgot-password-content">
                        <div className="success-icon">
                            <i className="fas fa-paper-plane"></i>
                        </div>
                        <h2>Reset Link Sent</h2>
                        <p>{message}</p>
                        <p>Please check your email and follow the instructions to reset your password.</p>

                        <div className="help-text">
                            <p>Didn't receive the email?</p>
                            <ul>
                                <li>Check your spam/junk folder</li>
                                <li>Wait a few minutes for delivery</li>
                                <li>Try requesting another reset</li>
                            </ul>
                        </div>

                        <div className="actions">
                            <button
                                onClick={() => setStatus('form')}
                                className="btn btn-secondary"
                            >
                                <i className="fas fa-redo"></i>
                                Send Another
                            </button>
                            <Link to="/login" className="btn btn-primary">
                                <i className="fas fa-sign-in-alt"></i>
                                Back to Login
                            </Link>
                        </div>
                    </div>
                </div>
                {renderStyles()}
            </div>
        );
    }

    return (
        <div className="forgot-password-page">
            <div className="forgot-password-container">
                <div className="forgot-password-content">
                    <Logo size="medium" usePublicLogo={true} />

                    <div className="reset-icon">
                        <i className="fas fa-key"></i>
                    </div>

                    <h2>Reset Your Password</h2>
                    <p>Enter your email address and we'll send you a link to reset your password.</p>

                    <form onSubmit={handleSubmit}>
                        {error && (
                            <div className="error-message">
                                <i className="fas fa-exclamation-circle"></i>
                                {error}
                            </div>
                        )}
                        <div className="form-group">
                            <label htmlFor="email">Email Address</label>
                            <input
                                type="email"
                                id="email"
                                value={email}
                                onChange={(e) => {
                                    setEmail(e.target.value);
                                    setError('');
                                }}
                                className="form-input"
                                placeholder="Enter your email address"
                                required
                            />
                            <i className="fas fa-envelope"></i>
                        </div>

                        <button
                            type="submit"
                            className="btn btn-primary w-full"
                            disabled={status === 'loading'}
                        >
                            {status === 'loading' ? (
                                <>
                                    <div className="spinner"></div>
                                    Sending Reset Link...
                                </>
                            ) : (
                                <>
                                    <i className="fas fa-paper-plane"></i>
                                    Send Reset Link
                                </>
                            )}
                        </button>
                    </form>

                    <div className="back-to-login">
                        <Link to="/login">
                            <i className="fas fa-arrow-left"></i>
                            Back to Login
                        </Link>
                    </div>
                </div>
            </div>
            {renderStyles()}
        </div>
    );
};

// Shared styles function
const renderStyles = () => (
    <style jsx>{`
        .forgot-password-page {
            min-height: 100vh;
            height: 100%;
            background: var(--gradient);
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 2rem;
            flex: 1;
        }

        .forgot-password-container {
            max-width: 500px;
            width: 100%;
            background: var(--white);
            border-radius: 20px;
            box-shadow: 0 20px 40px rgba(0,0,0,0.1);
            overflow: hidden;
        }

        .forgot-password-content {
            padding: 3rem;
            text-align: center;
        }

        .logo {
            display: flex;
            align-items: center;
            gap: 10px;
            margin-bottom: 2rem;
            justify-content: center;
        }

        .logo-icon {
            width: 40px;
            height: 40px;
            background: var(--gradient);
            border-radius: 8px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            font-weight: bold;
        }

        .logo-text {
            font-size: 1.5rem;
            font-weight: bold;
            background: var(--gradient);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
        }

        .reset-icon, .success-icon {
            width: 80px;
            height: 80px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 auto 1.5rem;
            font-size: 2.5rem;
        }

        .reset-icon {
            width: 60px;
            height: 60px;
            background: var(--gradient);
            color: white;
            font-size: 1.5rem;
        }

        .success-icon {
            background: #e8f5e8;
            color: #2e7d32;
        }

        h2 {
            color: var(--text-dark);
            margin-bottom: 1rem;
            font-size: 1.8rem;
        }

        p {
            color: var(--text-light);
            line-height: 1.6;
            margin-bottom: 1.5rem;
        }

        .help-text {
            background: #f8f9fa;
            padding: 1.5rem;
            border-radius: 10px;
            margin: 2rem 0;
            text-align: left;
        }

        .help-text p {
            font-weight: 600;
            color: var(--text-dark);
            margin-bottom: 1rem;
        }

        .help-text ul {
            margin: 0;
            padding-left: 1.5rem;
            color: var(--text-light);
        }

        .help-text li {
            margin-bottom: 0.5rem;
        }

        .form-group {
            margin-bottom: 1.5rem;
            position: relative;
            text-align: left;
        }

        .form-group label {
            display: block;
            margin-bottom: 0.5rem;
            color: var(--text-dark);
            font-weight: 500;
        }

        .form-group input {
            width: 100%;
            padding: 1rem;
            padding-left: 3rem;
            border: 2px solid #e0e0e0;
            border-radius: 10px;
            font-size: 1rem;
            transition: all 0.3s;
            outline: none;
        }

        .form-group input:focus {
            border-color: var(--primary-color);
            box-shadow: 0 0 0 3px rgba(231, 76, 60, 0.1);
        }

        .form-group i {
            position: absolute;
            left: 1rem;
            top: 50%;
            transform: translateY(-50%);
            color: var(--text-light);
            margin-top: 0.75rem;
        }

        .error-message {
            background: rgba(231, 76, 60, 0.1);
            color: var(--error);
            padding: 1rem;
            border-radius: 8px;
            margin-bottom: 1.5rem;
            display: flex;
            align-items: center;
            gap: 0.5rem;
            text-align: left;
        }

        .back-to-login {
            margin-top: 2rem;
            padding-top: 2rem;
            border-top: 1px solid #e0e0e0;
        }

        .back-to-login a {
            color: var(--primary-color);
            text-decoration: none;
            font-weight: 500;
            display: flex;
            align-items: center;
            gap: 0.5rem;
            justify-content: center;
        }

        .back-to-login a:hover {
            text-decoration: underline;
        }

        .actions {
            display: flex;
            gap: 1rem;
            justify-content: center;
            margin-top: 2rem;
            flex-wrap: wrap;
        }

        .spinner {
            width: 1rem;
            height: 1rem;
            border: 2px solid rgba(255, 255, 255, 0.3);
            border-top: 2px solid white;
            border-radius: 50%;
            animation: spin 1s linear infinite;
            margin-right: 0.5rem;
        }

        @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
        }

        @media (max-width: 768px) {
            .forgot-password-content {
                padding: 2rem;
            }
            
            .actions {
                flex-direction: column;
            }
        }
    `}</style>
);

export default ForgotPassword;